/**
 * Head Office Dashboard — dummy/constant data + shared types.
 *
 * Every presentational widget under `headOffice/` is "headless": it only owns
 * the design and receives its data via props. This file is the single source of
 * that data. When the real API is ready, swap these constants for the fetched
 * response — the components do not change.
 */

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

export interface CampusDatum {
  /** campus user_id — present when sourced from the API (used for drill-down). */
  campus_id?: number;
  name: string;
  initials: string;
  /** optional descriptive meta (e.g. class teacher) used by campus-scoped reuse */
  teacher?: string;
  meta?: string;
  students: number;
  books: number;
  pages: number;
  classes: number;
  quiz: number;
  adoption: number;
  trend: number;
  ageApprop: number;
  completion: number;
  active: number;
  assigned: number;
  inProgress: number;
  overdue: number;
  weekly: number[];
  tLogins: number;
  tAssignments: number;
  tActive: number;
  tTotal: number;
  classesTop: [string, number][];
  studentsTop: [string, number][];
  teachersTop: [string, number, number, number][]; // [name, assigned, completed, quiz]
}

export interface TeacherDatum {
  /** teacher user_id — needed to fetch the drill-down detail */
  teacher_id?: number;
  name: string;
  campus: string;
  assigned: number;
  completed: number;
  quiz: number;
}

export interface StudentDatum {
  /** student user_id — needed to fetch the drill-down detail */
  student_id?: number;
  name: string;
  meta: string;
  initials: string;
  books: number;
  badges: number;
  pages: number;
  time: string;
  quiz: number;
}

export interface KpiDatum {
  label: string;
  value: string;
  icon:
    | "campus"
    | "students"
    | "class"
    | "book"
    | "pages"
    | "teacher"
    | "quiz"
    | "time";
  delta: string;
  up: boolean;
  /** neutral delta chip (no up/down arrow, muted colour) */
  neutral?: boolean;
}

export interface MixSlice {
  name: string;
  pct: number;
  color: string;
}

export interface MixDimension {
  key: string;
  label: string;
  data: MixSlice[];
}

export interface GrowthMetric {
  key: string;
  label: string;
  color: string;
  unit: string;
  sub: string;
  /** derive the series for the currently selected range */
  derive: (range: RangeDatum) => number[];
}

export interface RangeDatum {
  label: string;
  months: string[];
  books: number[];
  pages: number[];
  delta: string;
}

export interface BrandingTheme {
  key: string; // "" = default aubergine
  label: string;
  group: "Light" | "Dark";
  swatch: string;
}

export interface WidgetMeta {
  key: WidgetKey;
  label: string;
  group: "core" | "proposed";
  hint: string;
}

export type WidgetKey =
  | "kpi"
  | "campuses"
  | "teachers"
  | "students"
  | "content"
  | "growth"
  | "adoption"
  | "comparison"
  | "mix"
  | "heatmap"
  | "teacherAdoption"
  | "assignment"
  // campus-scoped only (head office does not use it)
  | "alerts"
  // teacher-scoped only
  | "capacity";

/* ------------------------------------------------------------------ *
 * Palette helpers (shared avatar tints)
 * ------------------------------------------------------------------ */

export const AVATAR_TINTS = [
  { tint: "#FBF0FB", color: "#9A469B" },
  { tint: "#E7F6F7", color: "#1897A6" },
  { tint: "#FEF0E6", color: "#F9A11B" },
  { tint: "#FDECF2", color: "#EF437B" },
  { tint: "#F2F6E0", color: "#8CA00E" },
  { tint: "#EAF0F6", color: "#4F6C8F" },
];

/* ------------------------------------------------------------------ *
 * Date-range options (drive the growth line chart + adoption delta)
 * ------------------------------------------------------------------ */

export const RANGES: RangeDatum[] = [
  {
    label: "This Month",
    months: ["W1", "W2", "W3", "W4"],
    books: [820, 910, 1010, 1100],
    pages: [61, 68, 75, 82],
    delta: "vs last month",
  },
  {
    label: "Last 3 Months",
    months: ["May", "Jun", "Jul"],
    books: [3050, 3480, 3840],
    pages: [224, 258, 286],
    delta: "vs prior quarter",
  },
  {
    label: "Last 6 Months",
    months: ["Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    books: [1980, 2360, 2710, 3050, 3480, 3840],
    pages: [148, 171, 196, 224, 258, 286],
    delta: "vs prior 6 months",
  },
  {
    label: "This Year",
    months: [
      "Aug", "Sep", "Oct", "Nov", "Dec", "Jan",
      "Feb", "Mar", "Apr", "May", "Jun", "Jul",
    ],
    books: [1240, 1460, 1610, 1720, 1880, 1980, 2360, 2710, 3050, 3480, 3840, 3990],
    pages: [92, 108, 120, 128, 140, 148, 171, 196, 224, 258, 286, 297],
    delta: "vs last year",
  },
];

/* ------------------------------------------------------------------ *
 * Reading-growth metrics (multi-select line chart)
 * ------------------------------------------------------------------ */

export const GROWTH_METRICS: GrowthMetric[] = [
  {
    key: "total",
    label: "Total Readings",
    color: "#9A469B",
    unit: "",
    sub: "Books completed per period",
    derive: (r) => r.books,
  },
  {
    key: "cumulative",
    label: "Cumulative Readings",
    color: "#EF437B",
    unit: "",
    sub: "Running total of books completed",
    derive: (r) => {
      let s = 0;
      return r.books.map((v) => (s += v));
    },
  },
  {
    key: "outcomes",
    label: "Learning Outcomes",
    color: "#1897A6",
    unit: "%",
    sub: "Average quiz pass rate over time",
    derive: (r) =>
      [62, 64, 66, 67, 68, 69, 70, 72, 73, 74, 75, 76].slice(-r.months.length),
  },
  {
    key: "completion",
    label: "Book Completion Rate",
    color: "#F9A11B",
    unit: "%",
    sub: "Assigned books completed on time",
    derive: (r) =>
      [70, 72, 74, 75, 77, 78, 80, 81, 82, 83, 84, 85].slice(-r.months.length),
  },
  {
    key: "engagement",
    label: "Engagement / User",
    color: "#8CA00E",
    unit: "",
    sub: "Average books read per active student",
    derive: (r) =>
      [2.0, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.9, 3.0, 3.1, 3.3, 3.4].slice(
        -r.months.length,
      ),
  },
];

/* ------------------------------------------------------------------ *
 * Branding themes (the color panel in the header)
 * ------------------------------------------------------------------ */

/** solid primary colour per theme key — used to colour canvas charts, which
 *  can't resolve CSS variables. Keep in sync with theme.module.scss. */
export const THEME_PRIMARY: Record<string, string> = {
  "": "#9A469B",
  aubergine: "#9A469B",
  teal: "#1897A6",
  rose: "#EF437B",
  indigo: "#3E5C82",
  emerald: "#2F8F6B",
  graphite: "#475467",
  "dark-aubergine": "#C874C9",
  "dark-teal": "#3EC5D3",
  "dark-slate": "#7FA0CC",
};

export const BRANDING_THEMES: BrandingTheme[] = [
  { key: "", label: "Aubergine", group: "Light", swatch: "linear-gradient(120deg,#9A469B,#7E3A80)" },
  { key: "teal", label: "Teal", group: "Light", swatch: "linear-gradient(120deg,#1897A6,#127079)" },
  { key: "rose", label: "Rose", group: "Light", swatch: "linear-gradient(120deg,#EF437B,#CE2E63)" },
  { key: "indigo", label: "Indigo", group: "Light", swatch: "linear-gradient(120deg,#3E5C82,#2C4460)" },
  { key: "emerald", label: "Emerald", group: "Light", swatch: "linear-gradient(120deg,#2F8F6B,#226E51)" },
  { key: "graphite", label: "Graphite", group: "Light", swatch: "linear-gradient(120deg,#475467,#333D4B)" },
  { key: "dark-aubergine", label: "Dark Aubergine", group: "Dark", swatch: "linear-gradient(120deg,#241A2D,#C874C9)" },
  { key: "dark-teal", label: "Dark Teal", group: "Dark", swatch: "linear-gradient(120deg,#15262E,#3EC5D3)" },
  { key: "dark-slate", label: "Dark Slate", group: "Dark", swatch: "linear-gradient(120deg,#18202E,#7FA0CC)" },
];

/* ------------------------------------------------------------------ *
 * Customizable widgets (customize modal + report modal)
 * ------------------------------------------------------------------ */

export const WIDGET_META: WidgetMeta[] = [
  /* order matches the dashboard layout — the first 5 are fixed (never hideable) */
  { key: "kpi", label: "Network at a Glance", group: "core", hint: "5 totals" },
  { key: "growth", label: "Reading Growth Trends", group: "core", hint: "line chart" },
  { key: "mix", label: "Reading Mix", group: "core", hint: "genre/lang" },
  { key: "campuses", label: "The Reader Campuses", group: "core", hint: "ranked" },
  { key: "adoption", label: "Adoption Rate", group: "core", hint: "gauge" },
  { key: "teachers", label: "Top Teachers", group: "proposed", hint: "ranked" },
  { key: "students", label: "Top Students", group: "proposed", hint: "ranked" },
  { key: "content", label: "Books & Content Analytics", group: "proposed", hint: "breakdown" },
  { key: "teacherAdoption", label: "Teacher Adoption", group: "proposed", hint: "logins" },
  { key: "comparison", label: "Campus Comparison", group: "proposed", hint: "heatmap" },
  { key: "assignment", label: "Assignment Pipeline", group: "proposed", hint: "donut" },
];

export const REPORT_RANGES = [
  "This month",
  "Last 3 months",
  "Last 6 months",
  "Custom period",
];

/* ------------------------------------------------------------------ *
 * Reading Mix dimensions (donut, switched by dropdown)
 * ------------------------------------------------------------------ */

export const MIX_DIMENSIONS: MixDimension[] = [
  {
    key: "genre",
    label: "By Genre",
    data: [
      { name: "Adventure", pct: 28, color: "#9A469B" },
      { name: "Folk Tales", pct: 22, color: "#EF437B" },
      { name: "Science", pct: 16, color: "#1897A6" },
      { name: "Poetry", pct: 12, color: "#F9A11B" },
      { name: "History", pct: 12, color: "#C2D52E" },
      { name: "Islamic", pct: 10, color: "#58C0BF" },
    ],
  },
  {
    key: "language",
    label: "By Language",
    data: [
      { name: "Urdu", pct: 54, color: "#9A469B" },
      { name: "English", pct: 38, color: "#1897A6" },
      { name: "Other", pct: 8, color: "#F9A11B" },
    ],
  },
  {
    key: "age",
    label: "By Age Range",
    data: [
      { name: "Ages 4–7", pct: 28, color: "#9A469B" },
      { name: "Ages 8–11", pct: 41, color: "#EF437B" },
      { name: "Ages 12–15", pct: 25, color: "#1897A6" },
      { name: "Ages 15+", pct: 6, color: "#F9A11B" },
    ],
  },
  {
    key: "keyword",
    label: "By Keyword",
    data: [
      { name: "Moral Values", pct: 26, color: "#9A469B" },
      { name: "Animals", pct: 22, color: "#EF437B" },
      { name: "Friendship", pct: 18, color: "#1897A6" },
      { name: "Nature", pct: 16, color: "#F9A11B" },
      { name: "Courage", pct: 10, color: "#C2D52E" },
      { name: "Family", pct: 8, color: "#58C0BF" },
    ],
  },
];

/* ------------------------------------------------------------------ *
 * Network KPIs
 * ------------------------------------------------------------------ */

export const KPIS: KpiDatum[] = [
  { label: "Total Campuses", value: "6", icon: "campus", delta: "+1", up: true },
  { label: "Total Students", value: "7,777", icon: "students", delta: "+491", up: true },
  { label: "Total Screen Time", value: "1,204h", icon: "time", delta: "+9%", up: true },
  { label: "Total Books Read", value: "19,420", icon: "book", delta: "+14%", up: true },
  { label: "Total Pages Read", value: "1.60M", icon: "pages", delta: "-3%", up: false },
];

/* ------------------------------------------------------------------ *
 * Campuses
 * ------------------------------------------------------------------ */

export const CAMPUS_DATA: CampusDatum[] = [
  {
    name: "Ehya Main Campus", initials: "EM", students: 2247, books: 5820, pages: 486, classes: 84,
    quiz: 71, adoption: 78, trend: 12, ageApprop: 88, completion: 84, active: 82,
    assigned: 1240, inProgress: 186, overdue: 42, weekly: [62, 68, 71, 66, 74, 78, 82, 85],
    tLogins: 5.8, tAssignments: 142, tActive: 48, tTotal: 52,
    classesTop: [["Grade 5-A", 640], ["Grade 6-B", 560], ["Grade 5-C", 520], ["Grade 4-A", 470], ["Grade 6-A", 430]],
    studentsTop: [["Fatima Noor", 96], ["Maryam Iqbal", 77], ["Ali Hamza", 71], ["Ayaan Malik", 66], ["Sara Khan", 61]],
    teachersTop: [["Ms. Ayesha Khan", 320, 268, 82], ["Mr. Faisal Iqbal", 300, 244, 78], ["Ms. Nida Aslam", 280, 222, 75], ["Mr. Owais Raza", 260, 198, 72], ["Ms. Rabia Noor", 240, 180, 70]],
  },
  {
    name: "Ehya North Campus", initials: "EN", students: 1680, books: 4310, pages: 361, classes: 62,
    quiz: 69, adoption: 74, trend: 9, ageApprop: 85, completion: 80, active: 78,
    assigned: 980, inProgress: 150, overdue: 33, weekly: [54, 58, 63, 60, 66, 70, 73, 76],
    tLogins: 5.2, tAssignments: 118, tActive: 39, tTotal: 44,
    classesTop: [["Grade 6-A", 520], ["Grade 5-B", 480], ["Grade 4-C", 410], ["Grade 5-A", 360], ["Grade 3-B", 300]],
    studentsTop: [["Zain Ali", 88], ["Bilal Hussain", 70], ["Emaan Fatima", 64], ["Huzaifa Khan", 59], ["Areeba Shah", 55]],
    teachersTop: [["Mr. Bilal Ahmed", 290, 240, 79], ["Ms. Komal Nadeem", 270, 214, 76], ["Mr. Danish Ali", 250, 196, 73], ["Ms. Sadia Rauf", 230, 178, 71], ["Mr. Kashif Ali", 210, 160, 68]],
  },
  {
    name: "Ehya Gulberg Campus", initials: "EG", students: 1420, books: 3690, pages: 298, classes: 55,
    quiz: 67, adoption: 69, trend: 6, ageApprop: 83, completion: 77, active: 73,
    assigned: 860, inProgress: 132, overdue: 38, weekly: [48, 52, 57, 55, 60, 64, 67, 70],
    tLogins: 4.9, tAssignments: 104, tActive: 33, tTotal: 38,
    classesTop: [["Grade 5-A", 470], ["Grade 6-B", 420], ["Grade 4-A", 380], ["Grade 5-C", 330], ["Grade 3-A", 280]],
    studentsTop: [["Ahmed Raza", 81], ["Zoya Amir", 68], ["Hamza Tariq", 62], ["Iman Yousuf", 57], ["Talha Nadeem", 52]],
    teachersTop: [["Ms. Sana Malik", 275, 224, 77], ["Mr. Adeel Baig", 255, 202, 74], ["Ms. Mahnoor Ali", 235, 184, 72], ["Mr. Zeeshan Haq", 215, 166, 69], ["Ms. Fizza Ahmed", 195, 148, 66]],
  },
  {
    name: "Ehya DHA Campus", initials: "ED", students: 1180, books: 2980, pages: 241, classes: 44,
    quiz: 65, adoption: 66, trend: 4, ageApprop: 81, completion: 74, active: 70,
    assigned: 720, inProgress: 110, overdue: 29, weekly: [44, 47, 51, 49, 54, 57, 60, 63],
    tLogins: 4.4, tAssignments: 88, tActive: 27, tTotal: 32,
    classesTop: [["Grade 6-A", 410], ["Grade 5-B", 360], ["Grade 4-B", 320], ["Grade 5-A", 290], ["Grade 3-C", 240]],
    studentsTop: [["Hassan Sheikh", 72], ["Laiba Khan", 60], ["Usman Ali", 55], ["Noor Fatima", 50], ["Rehan Aziz", 46]],
    teachersTop: [["Ms. Hina Raza", 240, 190, 74], ["Mr. Junaid Iqbal", 220, 172, 71], ["Ms. Sidra Malik", 200, 154, 69], ["Mr. Waleed Khan", 180, 136, 66], ["Ms. Amna Riaz", 160, 118, 63]],
  },
  {
    name: "Ehya Model Town", initials: "MT", students: 910, books: 1980, pages: 162, classes: 34,
    quiz: 61, adoption: 58, trend: 2, ageApprop: 78, completion: 69, active: 62,
    assigned: 520, inProgress: 82, overdue: 24, weekly: [36, 39, 43, 41, 46, 49, 52, 55],
    tLogins: 3.8, tAssignments: 62, tActive: 19, tTotal: 24,
    classesTop: [["Grade 5-A", 320], ["Grade 6-B", 280], ["Grade 4-A", 240], ["Grade 5-C", 210], ["Grade 3-B", 170]],
    studentsTop: [["Eshaal Malik", 58], ["Abdullah Tariq", 49], ["Zainab Ali", 44], ["Musa Khan", 40], ["Hania Shah", 36]],
    teachersTop: [["Mr. Usman Tariq", 210, 158, 70], ["Ms. Beenish Ali", 190, 140, 67], ["Mr. Shahzad Raza", 170, 122, 64], ["Ms. Uzma Khan", 150, 106, 61], ["Mr. Naveed Iqbal", 130, 90, 58]],
  },
  {
    name: "New Test Campus ABC", initials: "NT", students: 340, books: 640, pages: 51, classes: 12,
    quiz: 48, adoption: 41, trend: -3, ageApprop: 71, completion: 58, active: 45,
    assigned: 180, inProgress: 28, overdue: 19, weekly: [22, 20, 26, 24, 19, 23, 18, 16],
    tLogins: 2.1, tAssignments: 18, tActive: 5, tTotal: 12,
    classesTop: [["Grade 5-A", 180], ["Grade 4-B", 140], ["Grade 6-A", 110], ["Grade 3-A", 90], ["Grade 5-C", 60]],
    studentsTop: [["Ibrahim Khan", 34], ["Aiza Malik", 28], ["Saad Ali", 24], ["Mahira Noor", 20], ["Yousuf Raza", 17]],
    teachersTop: [["Mr. Kamran Javed", 60, 28, 52], ["Ms. Farah Ali", 50, 22, 49], ["Mr. Asad Khan", 40, 18, 46], ["Ms. Nimra Shah", 30, 12, 43], ["Mr. Tariq Mir", 20, 8, 40]],
  },
];

/* ------------------------------------------------------------------ *
 * Network-wide top teachers / students
 * ------------------------------------------------------------------ */

export const TEACHER_DATA: TeacherDatum[] = [
  { name: "Ms. Ayesha Khan", campus: "Ehya Main Campus", assigned: 320, completed: 268, quiz: 82 },
  { name: "Mr. Bilal Ahmed", campus: "Ehya North Campus", assigned: 290, completed: 240, quiz: 79 },
  { name: "Ms. Sana Malik", campus: "Ehya Gulberg Campus", assigned: 275, completed: 224, quiz: 77 },
  { name: "Ms. Hina Raza", campus: "Ehya DHA Campus", assigned: 240, completed: 190, quiz: 74 },
  { name: "Mr. Usman Tariq", campus: "Ehya Model Town", assigned: 210, completed: 158, quiz: 70 },
];

export const STUDENT_DATA: StudentDatum[] = [
  { name: "Fatima Noor", meta: "Ehya Main · Grade 5 · Reading Master", initials: "FN", books: 96, badges: 8, pages: 14200, time: "62h", quiz: 94 },
  { name: "Zain Ali", meta: "Ehya North · Grade 6 · Explorer", initials: "ZA", books: 88, badges: 7, pages: 12900, time: "57h", quiz: 90 },
  { name: "Ahmed Raza", meta: "Ehya Gulberg · Grade 4 · Explorer", initials: "AR", books: 81, badges: 6, pages: 11100, time: "51h", quiz: 88 },
  { name: "Maryam Iqbal", meta: "Ehya Main · Grade 5 · Rising Star", initials: "MI", books: 77, badges: 6, pages: 10400, time: "48h", quiz: 86 },
  { name: "Hassan Sheikh", meta: "Ehya DHA · Grade 6 · Rising Star", initials: "HS", books: 72, badges: 5, pages: 9800, time: "44h", quiz: 84 },
];

export const GENRE_DATA: MixSlice[] = MIX_DIMENSIONS[0].data;
export const LANGUAGE_DATA: MixSlice[] = MIX_DIMENSIONS[1].data;

/* Books & content analytics summary tiles */
export const CONTENT_SUMMARY = {
  overallBooks: "19,420",
  mostReadGenre: { name: "Adventure", pct: "28%" },
  mostReadLanguage: { name: "Urdu", pct: "54%" },
  ageAppropriate: "84%",
};

/* Adoption gauge */
export const ADOPTION = {
  percent: 76,
  caption: "5,940 active readers of 7,777 enrolled",
  deltaPct: "+4%",
  ageAppropriate: "84%",
};

/* Campus comparison heatmap columns */
export const COMPARISON_COLS = [
  "Adoption",
  "Quiz Pass",
  "Age-Approp",
  "Completion",
  "Engagement",
];

/* Reading-activity heatmap week labels */
export const HEAT_COLS = ["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8"];

/* ------------------------------------------------------------------ *
 * Small formatting helpers shared by widgets
 * ------------------------------------------------------------------ */

/** rows per page inside every "View All" modal */
export const VIEW_ALL_PAGE_SIZE = 5;

/**
 * Both helpers are fed straight from API payloads (see `mapKpis`, which passes
 * `stat?.value`), so a field the response omits must render as 0 rather than
 * take the whole dashboard down with it.
 */
export const fmt = (n: number) => (Number(n) || 0).toLocaleString("en-US");

/**
 * Screen time comes from the API as whole minutes. Show it the way a reader
 * reads a clock — "45m", "3h 20m", "1,204h" — instead of a raw minute count.
 */
export const fmtMins = (n: any): string => {
  const mins = Math.round(Number(n) || 0);
  if (mins < 60) return mins + "m";
  const hrs = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest ? `${fmt(hrs)}h ${rest}m` : `${fmt(hrs)}h`;
};
export const pct = (v: number, max: number) => {
  const total = Number(max) || 0;
  if (!total) return "0%";
  return Math.round(((Number(v) || 0) / total) * 100) + "%";
};

/** heatmap cell background + foreground for a 0-100 value */
export const heatCell = (v: number) => {
  const a = 0.1 + (v / 100) * 0.78;
  return {
    bg: `rgba(154,70,155,${a.toFixed(2)})`,
    fg: a > 0.52 ? "#fff" : "#5A2A5B",
  };
};

/** activity-heatmap tint (single hue, no text) */
export const activityCell = (v: number) => {
  const a = 0.1 + (v / 100) * 0.82;
  return `rgba(154,70,155,${a.toFixed(2)})`;
};
